import classNames from 'classnames';
import React, { useContext, useMemo, useRef, useState, useCallback } from 'react';
import { noop } from 'underscore';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Collapse,
  useCollapseState,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { /* ResponsiveAd, */ AssetGroup } from '@bingads-webui/campaign-odata-schemas';
import { ValidatedLegacyCallToActionWithLanguageSection } from '@bingads-webui-campaign-react/legacy-call-to-action';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import {
  ValidatedBusinessNameWithRecommendationSection,
} from '@bingads-webui-campaign-react/pmax-text-input';
import {
  ImageListPortalContainerSection,
  LogoListPortalContainerSection,
} from '@bingads-webui-campaign-react/pmax-image-experience';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AdStatusSection } from '@bingads-webui-campaign-react/ad-status';

import { getBulkMMAEditorStyles } from './bulk-mma-convert-editor-style';
import { SearchMMAAdPreview } from '../search-mma-ad-preview';
import { errorMapper } from '../utils/errors-util';
import { ImageExperienceWithSuggestionPollerForBulk } from './image-experience-with-suggestion-poller';
import { BulkMMAConvertEditorHeader } from './bulk-mma-convert-editor-header';

const entity = 'ResponsiveAd';

export const BulkMMAConvertEditorContainer = ({
  data,
  errors,
  onDataChange,
  perfMarker,
  permissions,
  viewContext,
  genericImage,
  showAllValidationErrors,
  oDataConfig,
  selectedRowCount,
}) => {
  const i18n = useContext(I18nContext);
  const errorMapperWithI18n = useMemo(() => error => errorMapper(error, i18n), [i18n]);
  const campaignId = viewContext.currentCampaign && viewContext.currentCampaign.Id;
  const classes = useDefaultStyles(getBulkMMAEditorStyles);
  const [url, setUrl] = useState('');
  const businessName = data.BusinessName;
  const images = data.Images;
  const callToAction = data.CallToAction;
  const [ctaText, setCTAText] = useState(callToAction && callToAction.cta);

  const previewData = useMemo(() => ({
    shortHeadline: i18n.getString('Audience_Campaign_Ad_Preview_Default_ShortHeadline'),
    longHeadline: i18n.getString('Audience_Campaign_Ad_Preview_Default_LongHeadline'),
    description: i18n.getString('Audience_Campaign_Ad_Preview_Default_Description'),
    businessName: businessName || i18n.getString('Audience_Campaign_Ad_Preview_Default_BusinessName'),
    displayUrl: url,
    url: url || 'https://www.bing.com',
    callToAction: ctaText,
  }), [businessName, ctaText, i18n, url]);

  const [
    allAdSettingsSections,
    adSettingsExpandedSections,
    setAdSettingsExpandedSections,
  ] = useCollapseState(
    ['images', 'logos', 'business-name', 'call-to-action', 'ad-status'] /* all sections */,
    ['images', 'logos', 'business-name', 'call-to-action', 'ad-status'] /* initially expanded sections */
  );

  const imagesContainerRef = useRef(null);
  const logosContainerRef = useRef(null);
  const [imageExperiencePortalContainerState, setImageExperiencePortalContainerState] = useState({});
  const [previewImagesSharedState, setPreviewImagesSharedState] = useState({ previewImageId: null, cropImageObj: null });
  const [, setImageExperienceAddNewImageCallback] = useState(noop);

  const allAssetsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAdSettingsExpandedSections }),
    [setAdSettingsExpandedSections]
  );

  const onCallActionLanguageChange = useCallback((updates) => {
    setCTAText(updates.ctaText);
  }, []);

  return (
    <div className={classNames(classes.adEditorShell, 'bulk-mma-convertor', 'crud-audience-ad-react', 'crud-multimedia-ad-react')}>
      <div className={classNames('ad-edit-and-preview-container')}>
        <ActuationGroup actuationProps={{ isActive: showAllValidationErrors }}>
          <AttentionBeaconGroup>
            <FormErrorGroup formErrorProps={{ errors, showAllValidationErrors }}>
              <ValidationGroup
                validationProps={{
                  schema: AssetGroup,
                  onValidate: onDataChange,
                  data,
                  i18n,
                }}
              >
                <BulkMMAConvertEditorHeader i18n={i18n} selectedRowCount={selectedRowCount} />
                <div className={classes.headerStackStyles}>
                  <div className="server-errors">
                    <UnmappedFormErrors mapper={errorMapperWithI18n} />
                  </div>
                </div>
                <div className={classes.container}>
                  <div className={classes.leftStyles}>
                    <Collapse allPanelItems={allAdSettingsSections} checkItems skipItemsTypeCheck activeKeys={adSettingsExpandedSections} setActiveKeys={setAdSettingsExpandedSections}>
                      <ImageListPortalContainerSection
                        containerRef={imagesContainerRef}
                        containerState={imageExperiencePortalContainerState}
                        imageLimit={constants.DEFAULT_MAX_IMAGE}
                        expand={allAssetsExpand}
                        images={images}
                        classes={classes}
                        key="images"
                      />
                      <LogoListPortalContainerSection
                        containerRef={logosContainerRef}
                        containerState={imageExperiencePortalContainerState}
                        expand={allAssetsExpand}
                        images={images}
                        key="logos"
                      />
                      <ValidatedBusinessNameWithRecommendationSection
                        businessName={businessName}
                        expand={allAssetsExpand}
                        entity={entity}
                        key="business-name"
                      />
                      <ValidatedLegacyCallToActionWithLanguageSection
                        callToAction={callToAction}
                        isMultimediaAd
                        isDisplayPlusMMA={false}
                        key="call-to-action"
                        onChange={onCallActionLanguageChange}
                        isMMAAutomatedCTAOptInEnabled
                        isBulk
                      />
                      <AdStatusSection
                        status={data.AdStatus || 'Active'}
                        expand={allAssetsExpand}
                        key="ad-status"
                      />
                    </Collapse>
                  </div>
                  <div className={classes.rightStyles}>
                    <div className={classes.mmaAdImagePreviewCarousel}>
                      <SearchMMAAdPreview
                        data={data}
                        onDataChange={onDataChange}
                        permissions={permissions}
                        genericImage={genericImage}
                        previewTexts={previewData}
                        previewImagesSharedState={previewImagesSharedState}
                        setPreviewImagesSharedState={setPreviewImagesSharedState}
                      />
                    </div>
                    <div>
                      <ImageExperienceWithSuggestionPollerForBulk
                        permissions={permissions}
                        campaignId={campaignId}
                        previewImagesSharedState={previewImagesSharedState}
                        setPreviewImagesSharedState={setPreviewImagesSharedState}
                        setImageExperiencePortalContainerState={setImageExperiencePortalContainerState}
                        setImageExperienceAddNewImageCallback={setImageExperienceAddNewImageCallback}
                        genericImage={genericImage}
                        oDataConfig={oDataConfig}
                        data={data}
                        onDataChange={onDataChange}
                        ctaText={ctaText}
                        perfMarker={perfMarker}
                        imagesContainerRef={imagesContainerRef}
                        logosContainerRef={logosContainerRef}
                        url={url}
                        setUrl={setUrl}
                      />
                    </div>
                  </div>
                </div>
              </ValidationGroup>
            </FormErrorGroup>
          </AttentionBeaconGroup>
        </ActuationGroup>
      </div>
    </div>
  );
};

BulkMMAConvertEditorContainer.propTypes = {
  data: PropTypes.shape({
    FinalUrls: PropTypes.string,
    MobileFinalUrls: PropTypes.string,
    BusinessName: PropTypes.string,
    Headlines: PropTypes.arrayOf(PropTypes.string),
    LongHeadlines: PropTypes.arrayOf(PropTypes.string),
    Descriptions: PropTypes.arrayOf(PropTypes.string),
    Images: PropTypes.shape({
      ImageList: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    TrackingUrlTemplate: PropTypes.string,
    FinalUrlSuffix: PropTypes.string,
    UrlCustomParameters: PropTypes.string,
    FinalUrlForRecommendation: PropTypes.string,
    CallToAction: PropTypes.shape({
      cta: PropTypes.string,
      language: PropTypes.string,
    }),
    AdStatus: PropTypes.string,
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      AdPreviewLibrarySearchMMA: PropTypes.bool,
    }),
  }).isRequired,
  genericImage: PropTypes.string.isRequired,
  viewContext: PropTypes.shape({
    currentCampaign: PropTypes.shape({
      Id: PropTypes.string,
    }),
  }).isRequired,
  showAllValidationErrors: PropTypes.bool.isRequired,
  oDataConfig: PropTypes.shape({
    odataRootUrl: PropTypes.string,
    odataPath: PropTypes.string,
    odataToken: PropTypes.string,
  }).isRequired,
  selectedRowCount: PropTypes.number.isRequired,
};

BulkMMAConvertEditorContainer.defaultProps = {
};
